import { useVuelidate } from '@vuelidate/core'
import { useForm } from 'vee-validate'
import { ref, unref, type ComputedRef, watch, computed } from 'vue'
import * as z from 'zod'
import zodToVuelidateTypedSchema from '@/utils/zod/zod-to-vuelidate'
import zodToVeeValidateTypedSchema from '@/utils/zod/zod-to-vee-validate'
import useLegacyVuelidate from '@/composables/useLegacyVuelidate'

export default function useSwordForm(
  { validationSchema, formState }: { validationSchema: z.ZodSchema | ComputedRef<z.ZodSchema>, formState: object | ComputedRef },
) {
  const $v = ref()
  const vuelidateRules = ref(zodToVuelidateTypedSchema(unref(validationSchema)))
  const vm$V = useLegacyVuelidate(vuelidateRules, formState)
  const v$ = useVuelidate(vuelidateRules, formState)

  watch(
    [() => formState, () => validationSchema],
    () => {
      vuelidateRules.value = zodToVuelidateTypedSchema(unref(validationSchema))
    },
    { deep: true },
  )

  watch(() => vm$V, () => {
    $v.value = vm$V.value
  }, { deep: true, immediate: true })

  const veeValidationSchema = computed(() => zodToVeeValidateTypedSchema(unref(validationSchema)))

  const veeForm = useForm({
    validationSchema: veeValidationSchema,
    initialValues: { ...unref(formState) } as Record<string, unknown>,
  })

  // Sync formState changes with vee-validate's internal state
  watch(
    () => unref(formState),
    (newState) => {
      veeForm.setValues(newState as Record<string, unknown>)
    },
    { deep: true },
  )

  return {
    vuelidateRules,
    v$,
    $v,
    veeForm,
  }
}
